import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Flower2, Heart, Shield, BookOpen, Calendar, Bell,
  Smartphone, ArrowRight, ChevronDown, Award, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';

const Landing = () => {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const { unreadCount } = useNotifications();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex flex-col">

      {/* ===== HERO — Full viewport, single focus ===== */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-16 min-h-[85vh] relative">
        
        {/* Notification bell for logged-in users */}
        {isAuthenticated && (
          <button onClick={() => navigate('/notifications')} className="absolute top-6 right-6 p-2.5 rounded-xl bg-white/10 hover:bg-white/20 transition-colors" data-testid="landing-notif-bell">
            <Bell className="w-6 h-6 text-pink-200" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center" data-testid="landing-notif-badge">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
        )}

        {/* Logo */}
        <div className="w-20 h-20 rounded-3xl bg-white/15 backdrop-blur-sm flex items-center justify-center mb-8">
          <Flower2 className="w-11 h-11 text-pink-200" />
        </div>

        {/* Title */}
        <h1 className="text-5xl sm:text-6xl font-bold text-white text-center mb-4 tracking-tight">
          Fleur de Vie
        </h1>
        <p className="text-lg sm:text-xl text-pink-100/80 text-center max-w-md mb-10 leading-relaxed">
          Comprendre ton corps. Prendre soin de toi. En toute confiance.
        </p>

        {/* Single CTA */}
        {isAuthenticated ? (
          <div className="flex flex-col sm:flex-row gap-3 w-full max-w-sm">
            <Button onClick={() => navigate('/dashboard')}
              className="flex-1 h-14 bg-white text-gray-900 hover:bg-white/90 text-lg font-bold rounded-2xl shadow-xl" data-testid="go-dashboard-btn">
              Mon Tableau de bord
            </Button>
            <Button onClick={() => { logout(); navigate('/'); }}
              variant="outline" className="h-14 border-white/30 text-white hover:bg-white/10 rounded-2xl px-6">
              Deconnexion
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3 w-full max-w-sm">
            <Button onClick={() => navigate('/onboarding')}
              className="w-full h-14 bg-white text-gray-900 hover:bg-white/90 text-lg font-bold rounded-2xl shadow-xl" data-testid="start-btn">
              Commencer <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <button onClick={() => navigate('/login')} className="text-pink-200/70 hover:text-white text-sm transition-colors">
              J'ai deja un compte
            </button>
          </div>
        )}

        {/* Trust badges — glassmorphism 2x3 grid */}
        <div className="grid grid-cols-2 gap-3 mt-12 w-full max-w-sm">
          {[
            { icon: Shield, label: 'Securisee', iconColor: 'text-white', textColor: 'text-white' },
            { icon: Award, label: 'Gamifiee', iconColor: 'text-cyan-300', textColor: 'text-cyan-300' },
            { icon: Heart, label: 'Bienveillante', iconColor: 'text-pink-200', textColor: 'text-pink-200' },
            { icon: Sparkles, label: '+12 ans', iconColor: 'text-yellow-400', textColor: 'text-yellow-400' },
            { icon: Smartphone, label: 'Hors-ligne', iconColor: 'text-green-300', textColor: 'text-green-300' },
            { icon: Heart, label: '100% Gratuit', iconColor: 'text-rose-300', textColor: 'text-rose-300' }
          ].map((b) => (
            <div key={b.label} className="flex items-center justify-center gap-2.5 py-3.5 px-4 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 shadow-inner">
              <b.icon className={`w-5 h-5 ${b.iconColor}`} strokeWidth={1.5} />
              <span className={`text-sm font-medium ${b.textColor}`}>{b.label}</span>
            </div>
          ))}
        </div>

        {/* Scroll hint */}
        <button onClick={() => document.getElementById('value-props')?.scrollIntoView({ behavior: 'smooth' })}
          className="absolute bottom-8 text-white/30 hover:text-white/60 transition-colors animate-bounce">
          <ChevronDown className="w-6 h-6" />
        </button>
      </div>

      {/* ===== 3 VALUE PROPS — Quick scan ===== */}
      <div id="value-props" className="max-w-3xl mx-auto px-6 py-16">
        <div className="grid gap-8">
          {[
            { icon: BookOpen, color: 'from-pink-500 to-rose-600', title: '25 modules educatifs', desc: 'Contenu valide par des gynecologues et sages-femmes. Cycle, contraception, IST, bien-etre.' },
            { icon: Calendar, color: 'from-purple-500 to-indigo-600', title: 'Suivi personnalise', desc: 'Calendrier intelligent pour tes regles, ovulation, fertilite et rappels contraception.' },
            { icon: Shield, color: 'from-cyan-500 to-blue-600', title: 'Communaute securisee', desc: 'Girls Rooms dans ton ecole, questions anonymes, classement. Ton identite reste privee.' }
          ].map((item) => (
            <div key={item.title} className="flex items-start gap-5">
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center flex-shrink-0 shadow-lg`}>
                <item.icon className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg mb-1">{item.title}</h3>
                <p className="text-pink-200/70 text-sm leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ===== DOWNLOAD — Compact ===== */}
      <div id="download-section" className="max-w-lg mx-auto px-6 pb-16">
        <div className="flex justify-center">
          <a href="https://play.google.com/store/apps/details?id=co.median.android.bnawelk" target="_blank" rel="noopener noreferrer" data-testid="google-play-badge">
            <img src="https://play.google.com/intl/en_us/badges/static/images/badges/fr_badge_web_generic.png" alt="Disponible sur Google Play" className="h-16" />
          </a>
        </div>
      </div>

      {/* ===== FOOTER — Clean ===== */}
      <div className="border-t border-white/10 py-8 px-6 text-center">
        <p className="text-pink-200/50 text-sm">© 2026 Fleur de Vie — Tous droits reserves</p>
        <div className="flex justify-center gap-6 mt-3 text-xs text-pink-200/40">
          <Link to="/partners/apply" className="hover:text-white transition-colors">Partenaires</Link>
          <Link to="/terms" className="hover:text-white transition-colors">CGU</Link>
          <Link to="/privacy" className="hover:text-white transition-colors">Confidentialite</Link>
          <a href="mailto:support@fleurde-vie.com" className="hover:text-white transition-colors">Contact</a>
        </div>
        <p className="text-pink-200/30 text-xs mt-2">Application destinee aux personnes de 12 ans et plus</p>
      </div>
    </div>
  );
};

export default Landing;
